import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getBanca, getDatiAzienda } from '../services/api';
import { Container, Row, Col, Spinner, Alert } from 'react-bootstrap';
import '../print.css'; // Importa il CSS di stampa

function BanchePrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [banca, setBanca] = useState(null);
  const [datiAzienda, setDatiAzienda] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBancaAndAziendaData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [bancaResponse, aziendaResponse] = await Promise.all([
          getBanca(id),
          getDatiAzienda()
        ]);
        setBanca(bancaResponse.data);
        setDatiAzienda(aziendaResponse.data);
      } catch (err) {
        setError('Errore nel caricamento dei dati per la stampa.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchBancaAndAziendaData();
  }, [id]);

  useEffect(() => {
    const handleAfterPrint = () => {
      // Introduce a small delay to ensure the print dialog is fully dismissed
      setTimeout(() => {
        navigate('/banche');
      }, 100);
    };

    if (!loading && !error && banca && datiAzienda) {
      window.addEventListener('afterprint', handleAfterPrint);
      window.print();
    }

    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [loading, error, banca, datiAzienda, navigate]);

  const renderField = (label, value) => (
    <Row className="mb-2">
      <Col xs={5}><span className="label">{label}:</span></Col>
      <Col xs={7}><span className="value">{value || 'N/A'}</span></Col>
    </Row>
  );

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" />
        <p>Preparazione stampa banca...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error}</Alert>
        <p>Impossibile preparare la stampa.</p>
      </Container>
    );
  }

  if (!banca || !datiAzienda) {
    return (
      <Container className="mt-5">
        <Alert variant="warning">Dati della banca o dell'azienda non trovati per la stampa.</Alert>
      </Container>
    );
  }

  return (
    <div id="printable-area" className="portrait-page">
      <div className="print-container">
        <header className="print-header">
          <div className="company-details">
            {datiAzienda.logo_url && (
              <img
                src={`${process.env.REACT_APP_STATIC_FILES_BASE_URL}${datiAzienda.logo_url}`}
                alt="Logo Azienda"
                style={{ maxHeight: '150px', maxWidth: '150px', marginRight: '20px', objectFit: 'contain' }}
              />
            )}
          </div>
          <div className="print-info">
            <p><strong>Data di Stampa:</strong> {new Date().toLocaleDateString()}</p>
            <p><strong>ID Banca:</strong> {banca.id}</p>
          </div>
        </header>

        <main>
          <h2 className="document-title">Scheda Banca</h2>

          <section className="print-section">
            <div className="print-section-title">Dati Principali</div>
            <div className="print-section-content">
              <Row>
                <Col md={6}>
                  {renderField('Denominazione', banca.denominazione)}
                  {renderField('Referente', banca.referente)}
                </Col>
                <Col md={6}>
                  {renderField('Telefono', banca.telefono)}
                  {renderField('Cellulare', banca.cellulare)}
                  {renderField('Email', banca.email)}
                </Col>
              </Row>
            </div>
          </section>

          <section className="print-section">
            <div className="print-section-title">Dati Bancari</div>
            <div className="print-section-content">
              <Row>
                <Col md={6}>
                  {renderField('IBAN', banca.iban)}
                  {renderField('BIC/Swift', banca.bic)}
                </Col>
                <Col md={6}>
                  {renderField('Costo Transazione', banca.costo_transazione_percentuale != null ? `${banca.costo_transazione_percentuale}%` : 'N/A')}
                </Col>
              </Row>
            </div>
          </section>
        </main>

        <footer className="print-footer">
          <p>{datiAzienda.denominazione} - {datiAzienda.indirizzo} - {datiAzienda.cap} {datiAzienda.citta} ({datiAzienda.provincia}) - P.IVA {datiAzienda.piva}</p>
          <p>Email: {datiAzienda.email} - Tel: {datiAzienda.tel}</p>
        </footer>
      </div>
    </div>
  );
}

export default BanchePrint;